'use client';

import React, { RefCallback, useState } from 'react';

import {
    Box,
    BoxContent,
    Column,
    Columns,
    Divider,
    FlexBox,
    Justify,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyBold,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { EasyTradeInResult } from '@app-types/easy-trade-in';
import { IFundingDetail } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';
import { ProductDetailsModal } from '@components/product-details-modal';

import { getCurrentPhone } from '@helpers/simulated-cart';

import { TradeInStep } from '@constants/trade-in';

import { EasyTradeInCtaState, ProductCTA, SkeletonTicket } from '@product-detail/components';
import { CTASource } from '@product-detail/constants';
import { PricesDetails } from '@product-detail/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { TicketEasyTradeIn } from './ticket-easy-trade-in';
import { TicketFunding } from './ticket-funding';
import { TicketPlan } from './ticket-plan';
import { TicketProduct } from './ticket-product';
import { TicketTradeIn } from './ticket-trade-in';

export type ProductTicketProps = {
    type: WallType;
    currentProduct: ProductChildFull;
    plan?: MobilePlanType | null;
    openPlanModal: () => void;
    tradeInStep: TradeInStep;
    tradeInBonus: number;
    openTradeInModal: () => void;
    openShopAvailabilityModal: ClickEvent;
    selectedFunding?: IFundingDetail;
    isLoading: boolean;
    showRef: RefCallback<HTMLDivElement>;
    contract?: ContractType;
    prices: PricesDetails;
    isRenewal: boolean;
    idPu?: string;
    simulatedPortabilitySelected: boolean;
    easyTradeIn?: EasyTradeInCtaState;
    easyConfirmedResult?: EasyTradeInResult | null;
    returnedPhoneName: string | undefined;
};

export function Ticket({
    type,
    currentProduct,
    plan,
    openPlanModal,
    tradeInStep,
    tradeInBonus,
    openTradeInModal,
    openShopAvailabilityModal,
    selectedFunding,
    isLoading,
    showRef,
    contract,
    prices,
    isRenewal,
    idPu,
    simulatedPortabilitySelected,
    easyTradeIn,
    easyConfirmedResult,
    returnedPhoneName,
}: Readonly<ProductTicketProps>) {
    const [isShowPriceDetailsModal, setIsShowPriceDetailsModal] = useState(false);

    const { productPrices, funding } = prices;

    const phoneInSimulatedCart = getCurrentPhone(plan?.simulatedCart, currentProduct.gencode) ?? currentProduct;

    if (isLoading || productPrices.marketingAmount <= 0) {
        return <SkeletonTicket showRef={showRef} type={type} isRenewal={plan?.isOwned ?? false} />;
    }

    if (isRenewal && plan === undefined) {
        return null;
    }

    return (
        <Box shadowless className='remove-section-padding box-container'>
            <BoxContent>
                <Columns>
                    <Column>
                        <Title level={TitleLevels.FIVE}>{plan?.isOwned ? 'Mon offre actuelle' : 'Mes achats'}</Title>
                    </Column>
                </Columns>
                <TicketProduct
                    currentProduct={currentProduct}
                    onClickDetail={() => setIsShowPriceDetailsModal(true)}
                    isRenewal={isRenewal}
                    prices={prices}
                />
                {type === WallType.PHONE && tradeInStep === TradeInStep.CONFIRMED && !easyConfirmedResult && (
                    <TicketTradeIn
                        tradeInStep={tradeInStep}
                        tradeInBonus={tradeInBonus}
                        openTradeInModal={openTradeInModal}
                    />
                )}
                {type === WallType.PHONE && easyConfirmedResult && (
                    <>
                        <TicketEasyTradeIn result={easyConfirmedResult} returnedPhoneName={returnedPhoneName} />
                        {tradeInBonus > 0 && (
                            <FlexBox justify={Justify.SPACE_BETWEEN}>
                                <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless level={TextLevels.TWO}>
                                    Bonus reprise
                                </Text>
                                <InlinePrice level={TextLevels.TWO} amount={tradeInBonus} />
                            </FlexBox>
                        )}
                    </>
                )}

                {!isLoading && plan && (
                    <TicketPlan plan={plan} openPlanModal={openPlanModal} prices={prices} isRenewal={isRenewal} />
                )}
                <TicketFunding prices={prices} />
                <Divider />
                <div ref={showRef}>
                    <ProductCTA
                        type={type}
                        source={CTASource.TICKET}
                        currentProduct={currentProduct}
                        openShopAvailabilityModal={openShopAvailabilityModal}
                        plan={plan}
                        contract={contract}
                        isRenewal={isRenewal}
                        fundingKey={funding?.type}
                        idPu={idPu}
                        simulatedPortabilitySelected={simulatedPortabilitySelected}
                        easyTradeIn={easyTradeIn}
                    />
                </div>
                <ProductDetailsModal
                    plan={plan}
                    currentProduct={phoneInSimulatedCart}
                    onClose={() => setIsShowPriceDetailsModal(false)}
                    type={type}
                    selectedFunding={selectedFunding}
                    modalOpen={isShowPriceDetailsModal}
                />
            </BoxContent>
        </Box>
    );
}
